import { ReactNode } from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { Divider, IconButton, iconSizes, NavLink, Stack, Text, Title } from '@common/ui'
import { IconPlus, IconShieldLock } from '@common/ui/icons'
import { com } from '@eidu/web'
import { useSortedEntityTypes } from '../../io/useEntityTypes'
import PermissionError from './PermissionError'
import { iconForEntityTypeKind } from './kindViewRegistry'
import { isAdminUserType } from '../../util/entityType/isAdminUserType'
import useHasGlobalPermissions from '../permission/useHasGlobalPermissions'
import GlobalAction = com.eidu.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import EntityType = com.eidu.entity.type.EntityType
import { buildSidebarSectionsFromEntityTypes } from '../../components/navigation/SidebarSection'
import type { SidebarGroup } from '../../domain/kindRegistry'

const SIDEBAR_GROUP_ORDER: readonly SidebarGroup[] = ['entity', 'document', 'asset']

const EntityTypesPage = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const {
    hasPermission: hasGlobalPermission,
    error: permissionsError,
    loading: permissionsLoading,
  } = useHasGlobalPermissions(GlobalAction.ManageEntityTypes)

  const entityTypes = useSortedEntityTypes()

  const adminEntityType = entityTypes?.find(
    (type) => type.kind.equals(EntityTypeKind.Companion.User) && isAdminUserType(type.name)
  )

  type SectionItem = { id: EntityType['id']; name: string; icon: ReactNode }
  type Section = { title: string; items: SectionItem[]; dividerBefore?: boolean }

  const adminItems: SectionItem[] = []
  if (adminEntityType) {
    adminItems.push({
      id: adminEntityType.id,
      name: 'Admin Users',
      icon: <IconShieldLock size={iconSizes.sm} stroke={1.5} />,
    })
  }

  const adminEntityTypeId = adminEntityType?.id.toString()
  const visibleEntityTypes = (entityTypes ?? []).filter((type) => type.id.toString() !== adminEntityTypeId)

  const kindSections = buildSidebarSectionsFromEntityTypes(
    visibleEntityTypes,
    (type): SectionItem => ({ id: type.id, name: type.name, icon: iconForEntityTypeKind(type.kind) })
  )

  const sections: Section[] = []

  const pushSection = (section: Section) => {
    if (!section.items.length) return
    sections.push({ ...section, dividerBefore: sections.length > 0 })
  }

  pushSection({ title: 'Administration', items: adminItems })

  for (const sidebarGroup of SIDEBAR_GROUP_ORDER) {
    const groupSections = kindSections.filter((s) => s.sidebarGroup === sidebarGroup && s.items.length > 0)
    groupSections.forEach((s) => pushSection({ title: s.title, items: s.items }))
  }

  if (permissionsLoading) return null

  if (permissionsError) return <PermissionError missingPermittedActions={[GlobalAction.ManageEntityTypes]} />

  return (
    (hasGlobalPermission(GlobalAction.ManageEntityTypes) && (
      <>
        <IconButton
          variant="filled"
          size="xl"
          radius="xl"
          aria-label="Create entity type"
          onClick={() => navigate('new', { relative: 'path' })}
          style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 10 }}
        >
          <IconPlus size={iconSizes.md} stroke={1.5} />
        </IconButton>
        <Stack gap="md" p="lg">
          <Title order={2} mb="xs">
            Types
          </Title>
          {sections.map(({ title, items }) => (
            <Stack key={title} gap="xxs">
              <Text size="xs" tt="uppercase" fw={600} lts={1.5} lh={1} pb="xxs">
                {title}
              </Text>
              <Divider mb="xxs" />
              <Stack gap="xxs">
                {items.map(({ name, id, icon }) => {
                  const path = `/types/${id.toString()}`
                  return (
                    <NavLink
                      key={`entity-type-${id}`}
                      href={path}
                      label={<Text size="sm">{name}</Text>}
                      leftSection={icon}
                      active={!!matchPath({ path, end: false }, location.pathname)}
                      onClick={(event) => {
                        event.preventDefault()
                        navigate(path)
                      }}
                    />
                  )
                })}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </>
    )) ||
    PermissionError({ missingPermittedActions: [GlobalAction.ManageEntityTypes] })
  )
}

export default EntityTypesPage
